/**
 * Trending Keywords — detects keywords spiking above their baseline
 * frequency across all event feeds.
 *
 * - Tracks keyword frequency in 1-hour buckets
 * - 24-hour rolling window
 * - Spike detection: 3x hourly baseline triggers trending flag
 * - Excludes common stop words and source names
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { TrendingKeyword } from './types';
import { getEventTimestamp } from './types';

const ONE_HOUR_MS = 60 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * ONE_HOUR_MS;
const SPIKE_THRESHOLD = 3.0; // 3x baseline to trigger trending
const MIN_COUNT = 3; // Minimum occurrences in current window to consider
const MIN_KEYWORD_LENGTH = 4;
const MAX_KEYWORD_LENGTH = 40;

// ---------------------------------------------------------------------------
// Stop words — common words to exclude from trending
// ---------------------------------------------------------------------------

const STOP_WORDS = new Set([
  // English stop words
  'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her',
  'was', 'one', 'our', 'out', 'has', 'have', 'been', 'will', 'with', 'this',
  'that', 'from', 'they', 'been', 'said', 'each', 'which', 'their', 'time',
  'will', 'about', 'would', 'there', 'could', 'other', 'after', 'than',
  'some', 'what', 'also', 'more', 'very', 'when', 'just', 'into', 'over',
  'such', 'only', 'most', 'made', 'many', 'then', 'them', 'same', 'much',
  'being', 'those', 'does', 'your', 'here', 'were', 'says', 'says', 'may',
  'should', 'between', 'these', 'while', 'under', 'before', 'during',
  'where', 'still', 'first', 'report', 'reports', 'according', 'update',
  'news', 'latest', 'breaking', 'live', 'read', 'watch', 'video',
  // Source names
  'reuters', 'associated', 'press', 'bloomberg', 'guardian', 'times',
  'usgs', 'gdacs', 'acled', 'gdelt', 'firms', 'opensky',
  // Common news filler
  'new', 'year', 'years', 'people', 'state', 'world', 'global',
  'international', 'national', 'official', 'officials',
]);

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

interface HourlyBucket {
  timestamp: number;
  keywords: Map<string, number>;
}

const _buckets: HourlyBucket[] = [];
const MAX_BUCKETS = 24;

/** Floor a timestamp to the start of its hour. */
function floorToHour(ms: number): number {
  return Math.floor(ms / ONE_HOUR_MS) * ONE_HOUR_MS;
}

/** Extract significant keywords from text. */
function extractKeywords(text: string): string[] {
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter(w =>
      w.length >= MIN_KEYWORD_LENGTH &&
      w.length <= MAX_KEYWORD_LENGTH &&
      !STOP_WORDS.has(w) &&
      !/^\d+$/.test(w) // Exclude pure numbers
    );

  // Deduplicate within a single text
  return [...new Set(words)];
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Detect trending keywords from the current event set.
 *
 * Updates internal 1-hour buckets, computes hourly baseline over 24h,
 * and flags keywords at 3x or more above baseline.
 */
export function detectTrendingKeywords(events: NormalizedEvent[]): TrendingKeyword[] {
  const now = Date.now();
  const currentWindowStart = floorToHour(now);
  const cutoff = now - TWENTY_FOUR_HOURS_MS;

  // Build current window keyword counts from recent events
  const currentKeywords = new Map<string, number>();
  const firstSeen = new Map<string, string>();

  for (const event of events) {
    const ts = getEventTimestamp(event);
    if (ts < currentWindowStart) continue;

    const text = `${event.title} ${event.description ?? ''}`;
    const keywords = extractKeywords(text);

    for (const kw of keywords) {
      currentKeywords.set(kw, (currentKeywords.get(kw) ?? 0) + 1);
      if (!firstSeen.has(kw)) {
        firstSeen.set(kw, event.timestamp);
      }
    }
  }

  // Also record into historical buckets for events in older windows
  // (Only process events we haven't bucketed yet — by checking if bucket exists)
  const existingBucketIdx = _buckets.findIndex(b => b.timestamp === currentWindowStart);
  if (existingBucketIdx >= 0) {
    _buckets[existingBucketIdx] = { timestamp: currentWindowStart, keywords: currentKeywords };
  } else {
    _buckets.push({ timestamp: currentWindowStart, keywords: currentKeywords });
  }

  // Record older events into their respective buckets (only create new buckets,
  // never re-add to existing ones to prevent count inflation across ticks)
  for (const event of events) {
    const ts = getEventTimestamp(event);
    if (ts >= currentWindowStart || ts < cutoff) continue;

    const bucketTs = floorToHour(ts);
    // Skip if this bucket already exists — it was populated on a previous tick
    if (_buckets.some(b => b.timestamp === bucketTs)) continue;

    const bucket: HourlyBucket = { timestamp: bucketTs, keywords: new Map() };

    // Collect all events in this same hour bucket
    for (const e of events) {
      const eTs = getEventTimestamp(e);
      if (floorToHour(eTs) !== bucketTs) continue;

      const text = `${e.title} ${e.description ?? ''}`;
      const keywords = extractKeywords(text);
      for (const kw of keywords) {
        bucket.keywords.set(kw, (bucket.keywords.get(kw) ?? 0) + 1);
      }
    }

    _buckets.push(bucket);
  }

  // Trim old buckets
  while (_buckets.length > MAX_BUCKETS) {
    // Remove the oldest
    let oldestIdx = 0;
    let oldestTs = Infinity;
    for (let i = 0; i < _buckets.length; i++) {
      if (_buckets[i].timestamp < oldestTs) {
        oldestTs = _buckets[i].timestamp;
        oldestIdx = i;
      }
    }
    _buckets.splice(oldestIdx, 1);
  }

  // Compute baseline: average count per keyword across historical buckets (excluding current)
  const historicalBuckets = _buckets.filter(b => b.timestamp < currentWindowStart && b.timestamp >= cutoff);

  if (historicalBuckets.length === 0) {
    // Not enough history for baseline comparison
    return [];
  }

  const baselineTotals = new Map<string, number>();
  for (const bucket of historicalBuckets) {
    for (const [kw, count] of bucket.keywords) {
      baselineTotals.set(kw, (baselineTotals.get(kw) ?? 0) + count);
    }
  }

  const numHistorical = historicalBuckets.length;
  const trending: TrendingKeyword[] = [];

  for (const [keyword, count] of currentKeywords) {
    if (count < MIN_COUNT) continue;

    const baselineTotal = baselineTotals.get(keyword) ?? 0;
    const baseline = baselineTotal / numHistorical;

    // Must have some historical presence to compute meaningful ratio
    // (completely new keywords get a floor baseline of 0.5)
    const effectiveBaseline = Math.max(0.5, baseline);
    const ratio = count / effectiveBaseline;

    if (ratio >= SPIKE_THRESHOLD) {
      trending.push({
        keyword,
        count,
        baseline: Math.round(effectiveBaseline * 10) / 10,
        ratio: Math.round(ratio * 10) / 10,
        firstSeen: firstSeen.get(keyword) ?? new Date().toISOString(),
      });
    }
  }

  // Sort by ratio descending (most spiking first)
  trending.sort((a, b) => b.ratio - a.ratio);

  // Return top 20
  return trending.slice(0, 20);
}
